/**
 * The rules the item editor reads back, as pure functions.
 *
 * The payload a save writes is `item-payload.ts`. This is the other half: the
 * values the editor *shows* the merchant while they type — which of the three
 * kinds a choice group is, and what the dish costs while an offer runs.
 *
 * They are separate from the screen for the same reason the payload is: a rule
 * that lives in a component's JSX is one a second reader — another view, the
 * customer's menu — can quietly disagree with. A rule that lives here is one
 * every caller shares and a test covers.
 */

import { parseMoneyInput } from '@yummy/shared';

/**
 * How a group renders on the customer menu, which is decided by what it does to
 * the price. The three are not three settings — they are the two stored ones
 * (`price_mode`) plus the one the prices imply: a delta group whose choices are
 * all free changes nothing, which is a real and common case (sugar, ice, spice).
 */
export type GroupKind =
  /** Absolute prices: each choice is a version of the dish, priced on its own. */
  | 'sets'
  /** A delta whose choices all cost nothing: add-ons that do not change the price. */
  | 'free'
  /** A delta with at least one priced choice: add-ons that add to the price. */
  | 'adds';

/** The part of a group this module needs: how it prices, and its typed prices. */
export interface GroupShape {
  priceMode: 'absolute' | 'delta';
  choices: Array<{ price: string }>;
}

/**
 * Which of the three kinds a group is.
 *
 * An absolute group is "sets" even when every choice is zero: it owns the price,
 * and the all-free warning is a separate matter — showing it as "does not change
 * the price" would hide the mistake rather than surface it.
 *
 * @param group The group's price mode and typed choice prices.
 * @returns Its kind.
 */
export function groupKind(group: GroupShape): GroupKind {
  if (group.priceMode === 'absolute') return 'sets';
  const priced = group.choices.some((choice) => (parseMoneyInput(choice.price) ?? 0) > 0);
  return priced ? 'adds' : 'free';
}

/** Whether a group's choices are free, used for the label on each chip. */
export function choiceIsFree(price: string): boolean {
  return (parseMoneyInput(price) ?? 0) === 0;
}

/**
 * What the customer pays while an offer runs.
 *
 * The same arithmetic the Offers tab has always used, lifted so the inline offer
 * in the new layout and the consequence line in the old one cannot drift: a
 * percentage is floored to the cent, an amount is rounded, and the result never
 * goes below zero.
 *
 * @param priceCents The dish's price in cents, or null when it has none yet.
 * @param kind Percent off or a fixed amount.
 * @param valueText The typed value, as the form holds it.
 * @returns The price in cents, or null when there is not yet a figure to show.
 */
export function customerPaysCents(
  priceCents: number | null,
  kind: 'percent' | 'amount',
  valueText: string
): number | null {
  // An empty field means "no offer yet"; `0` is a typed figure and is shown.
  if (priceCents === null || valueText.trim() === '') return null;
  const value = Number(valueText);
  if (!Number.isFinite(value)) return null;
  const off =
    kind === 'percent' ? Math.floor((priceCents * value) / 100) : Math.round(value * 100);
  return Math.max(0, priceCents - off);
}
